<script setup lang="ts">
/**
 * La liste des surveillances (écran 1, S:1371-1455 et `rendreListe`
 * S:5190-5290), en lecture.
 *
 * Déjà triée : les surveillances à traiter d'abord, celles « pour information »
 * ensuite, repliées sous leur titre ; dans chaque groupe, alarmes actives non
 * acquittées d'abord, désactivées en dernier (S:5031). Le groupe « pour
 * information » n'existe que si la nature est servie. Filtre d'état et
 * recherche sur ce qui est servi : le lieu, l'équipement, le nom du type.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useTypesStore } from "@modules/alarmes-v2/stores/types.store"
import {
    CODES_A_TRAITER,
    ORDRE_DES_ETATS,
    estPourInformation,
    etatDeLaLigne,
    type CodeEtatLigne,
} from "@modules/alarmes-v2/utils/etats"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// TYPES & INTERFACES
// ================================
/** Les valeurs du filtre d'état (S:1386-1395). */
type FiltreEtat = "tous" | "a_traiter" | CodeEtatLigne

/** Une ligne prête à trier et à filtrer. */
interface Ligne {
    surveillance: AlarmesV2Surveillance
    alarme: AlarmesV2Alarme | null
    code: CodeEtatLigne
    info: boolean
    texte: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Les surveillances lues. */
        surveillances: AlarmesV2Surveillance[]
        /** L'alarme ouverte de chaque surveillance, par `_id_instance`. */
        alarmes?: Record<string, AlarmesV2Alarme>
        /** Les noms servis avec les alarmes. */
        personnes?: Record<string, AlarmesV2Personne>
        /** Relit la liste après un acquittement (lot F6) ; vrai si elle a abouti. */
        relire?: () => Promise<boolean>
    }>(),
    {
        alarmes: () => ({}),
        personnes: () => ({}),
        relire: () => Promise.resolve(false),
    }
)

/**
 * Les options du filtre d'état, dans l'ordre de la maquette.
 */
const FILTRES: FiltreEtat[] = [
    "tous",
    "a_traiter",
    "declenchee",
    "prise_en_compte",
    "retour_non_vu",
    "armee",
    "non_surveillee",
    "desactivee",
]

/**
 * Le catalogue, pour chercher sur le nom du type.
 */
const typesStore = useTypesStore()

/**
 * Le filtre d'état choisi.
 */
const filtreEtat = ref<FiltreEtat>("tous")

/**
 * Le texte cherché.
 */
const recherche = ref("")

/**
 * Vrai quand le groupe « pour information » est déplié.
 */
const infoOuvert = ref(false)

/**
 * Toutes les lignes, avec leur état et leur texte de recherche.
 */
const getLignes = computed((): Ligne[] =>
    props.surveillances.map((s) => {
        const alarme = props.alarmes[s._id] ?? null
        const nomType = typesStore.nomDuType(s.type_code, s.type_version) ?? ""
        return {
            surveillance: s,
            alarme,
            code: etatDeLaLigne(
                s,
                alarme,
                t as unknown as Traduire,
                props.personnes
            ).code,
            info: estPourInformation(s),
            texte: `${s.objet?.site_nom ?? ""} ${s.objet?.nom ?? ""} ${nomType}`.toLowerCase(),
        }
    })
)

/**
 * Les lignes retenues par le filtre et la recherche, triées.
 */
const getRetenues = computed((): Ligne[] => {
    const cherche = recherche.value.trim().toLowerCase()
    return getLignes.value
        .filter((l) => {
            if (
                filtreEtat.value === "a_traiter" &&
                !CODES_A_TRAITER.includes(l.code)
            )
                return false
            if (
                !["tous", "a_traiter"].includes(filtreEtat.value) &&
                l.code !== filtreEtat.value
            )
                return false
            return cherche === "" || l.texte.includes(cherche)
        })
        .sort(
            (a, b) =>
                ORDRE_DES_ETATS[a.code] - ORDRE_DES_ETATS[b.code] ||
                (a.surveillance._id < b.surveillance._id ? -1 : 1)
        )
})

/**
 * Les lignes à traiter, toujours visibles.
 */
const getATraiter = computed((): Ligne[] =>
    getRetenues.value.filter((l) => !l.info)
)

/**
 * Les lignes pour information, repliées sous leur titre.
 */
const getPourInfo = computed((): Ligne[] =>
    getRetenues.value.filter((l) => l.info)
)

/**
 * Le titre du groupe « pour information » (S:5236-5237).
 */
const getTitreInfo = computed((): string => {
    const n = getPourInfo.value.length
    const enCours = getPourInfo.value.filter(
        (l) => l.code === "declenchee"
    ).length
    const titre =
        n > 1
            ? t("alarmesV2.liste.groupeInfoPlusieurs", { n })
            : t("alarmesV2.liste.groupeInfoUne")
    return enCours > 0
        ? titre + t("alarmesV2.liste.groupeInfoEnCours", { n: enCours })
        : titre
})

/**
 * Le pied de liste : combien de surveillances le filtre retient (S:5283).
 */
const getPied = computed((): string => {
    const valeurs = {
        n: getRetenues.value.length,
        total: props.surveillances.length,
    }
    return getRetenues.value.length > 1
        ? t("alarmesV2.liste.piedPlusieurs", valeurs)
        : t("alarmesV2.liste.piedUne", valeurs)
})

/**
 * Déplie ou replie le groupe « pour information ».
 */
function onBasculerInfo(): void {
    infoOuvert.value = !infoOuvert.value
}
</script>

<template>
    <!-- ================================ -->
    <!-- FILTRES -->
    <!-- ================================ -->
    <div class="flex flex-wrap items-end gap-4 px-4 py-3">
        <label class="flex flex-col gap-1 text-xs text-surface-700">
            {{ t("alarmesV2.liste.recherche") }}
            <input
                v-model="recherche"
                type="search"
                class="rounded border border-surface-300 px-2 py-1 text-sm"
                :placeholder="t('alarmesV2.liste.rechercheAide')"
                data-test="recherche"
            />
        </label>
        <label class="flex flex-col gap-1 text-xs text-surface-700">
            {{ t("alarmesV2.liste.filtreEtat") }}
            <select
                v-model="filtreEtat"
                class="rounded border border-surface-300 px-2 py-1 text-sm"
                data-test="filtre-etat"
            >
                <option v-for="f in FILTRES" :key="f" :value="f">
                    {{ t(`alarmesV2.liste.filtres.${f}`) }}
                </option>
            </select>
        </label>
    </div>

    <!-- ================================ -->
    <!-- LISTE -->
    <!-- ================================ -->
    <div class="overflow-x-auto">
        <table class="w-full text-left">
            <thead class="bg-surface-50 text-xs uppercase text-surface-600">
                <tr>
                    <th scope="col" class="px-3 py-2">
                        {{ t("alarmesV2.liste.colonnes.etat") }}
                    </th>
                    <th scope="col" class="px-3 py-2">
                        {{ t("alarmesV2.liste.colonnes.equipement") }}
                    </th>
                    <th scope="col" class="px-3 py-2">
                        {{ t("alarmesV2.liste.colonnes.type") }}
                    </th>
                    <th scope="col" class="px-3 py-2">
                        {{ t("alarmesV2.liste.colonnes.mesure") }}
                    </th>
                    <th scope="col" class="px-3 py-2">
                        {{ t("alarmesV2.liste.colonnes.dit") }}
                    </th>
                    <th scope="col" class="px-3 py-2">
                        <span class="sr-only">{{
                            t("alarmesV2.liste.colonnes.actions")
                        }}</span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="getRetenues.length === 0">
                    <td colspan="6" class="px-3 py-4 text-sm text-surface-600">
                        {{ t("alarmesV2.liste.aucunFiltre") }}
                    </td>
                </tr>
                <LigneSurveillance
                    v-for="l in getATraiter"
                    :key="l.surveillance._id"
                    :surveillance="l.surveillance"
                    :alarme="l.alarme"
                    :personnes="personnes"
                    :relire="relire"
                />
                <tr
                    v-if="getPourInfo.length > 0"
                    class="border-t border-surface-200 bg-surface-50"
                >
                    <td colspan="6" class="px-3 py-2">
                        <button
                            type="button"
                            class="text-sm font-medium text-surface-800"
                            :aria-expanded="infoOuvert ? 'true' : 'false'"
                            data-test="repli-info"
                            @click="onBasculerInfo"
                        >
                            <span aria-hidden="true">{{
                                infoOuvert ? "▾" : "▸"
                            }}</span>
                            {{ getTitreInfo }}
                        </button>
                        <span class="ml-2 text-xs text-surface-600">{{
                            t("alarmesV2.liste.groupeInfoAide")
                        }}</span>
                    </td>
                </tr>
                <template v-if="infoOuvert">
                    <LigneSurveillance
                        v-for="l in getPourInfo"
                        :key="l.surveillance._id"
                        :surveillance="l.surveillance"
                        :alarme="l.alarme"
                        :personnes="personnes"
                        :relire="relire"
                    />
                </template>
            </tbody>
        </table>
    </div>

    <!-- ================================ -->
    <!-- PIED -->
    <!-- ================================ -->
    <p
        class="border-t border-surface-200 px-4 py-2 text-xs text-surface-600"
        data-test="pied"
    >
        {{ getPied }}
    </p>
</template>
